import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Home, Gauge, Building2, FileText, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { fadeUp } from "./anim";
import api from "@/lib/api";

// Count-up animation that runs once the element enters the viewport.
const useCountUp = (target, run, duration = 1400) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!run || !target) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setValue(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, run, duration]);
  return value;
};

const Counter = ({ icon: Icon, value, label, run, testid }) => {
  const n = useCountUp(value, run);
  return (
    <div className="rounded-xl border bg-card p-6 text-center" data-testid={testid}>
      <Icon className="mx-auto h-6 w-6 text-primary" />
      <div className="mt-3 font-display text-3xl font-semibold tabular-nums sm:text-4xl" data-testid={`${testid}-value`}>
        {n.toLocaleString()}<span className="text-primary">+</span>
      </div>
      <div className="mt-1 text-xs text-muted-foreground sm:text-sm">{label}</div>
    </div>
  );
};

const Stars = ({ n = 5 }) => (
  <div className="flex justify-center gap-0.5" aria-label={`${n} out of 5 stars`}>
    {Array.from({ length: n }).map((_, i) => (
      <Star key={i} className="h-4 w-4 fill-[hsl(var(--risk-medium))] text-[hsl(var(--risk-medium))]" />
    ))}
  </div>
);

export const SocialProof = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [stats, setStats] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/stats/public").then((r) => setStats(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data.testimonials || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (testimonials.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % testimonials.length), 8000);
    return () => clearInterval(t);
  }, [testimonials]);

  const COUNTERS = [
    { icon: Home, key: "hosts_registered", label: "Hosts registered", testid: "counter-hosts" },
    { icon: Gauge, key: "assessments_run", label: "Risk assessments run", testid: "counter-assessments" },
    { icon: Building2, key: "properties_monitored", label: "Properties monitored", testid: "counter-properties" },
    { icon: FileText, key: "reports_generated", label: "Reports generated", testid: "counter-reports" },
  ];

  const active = testimonials[idx];

  return (
    <section className="border-y bg-secondary/40">
      <div ref={ref} className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <motion.div {...fadeUp} className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-3xl font-semibold tracking-tight" data-testid="social-proof-heading">
            Trusted by hosts growing every day
          </h2>
          <p className="mt-3 text-muted-foreground">A growing community of Australian short-stay hosts protecting their properties.</p>
        </motion.div>

        <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="social-proof-counters">
          {COUNTERS.map((c) => (
            <Counter key={c.key} icon={c.icon} value={stats?.[c.key] || 0} label={c.label} run={inView} testid={c.testid} />
          ))}
        </div>

        {active && (
          <motion.div {...fadeUp} className="mx-auto mt-12 max-w-2xl" data-testid="testimonial-carousel">
            <div className="rounded-2xl border bg-card p-8 text-center shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
              <Stars n={active.rating || 5} />
              <motion.blockquote
                key={idx}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="mt-4 font-display text-lg leading-relaxed text-foreground"
                data-testid="testimonial-quote"
              >
                &ldquo;{active.quote}&rdquo;
              </motion.blockquote>
              <p className="mt-4 text-sm font-medium text-muted-foreground">
                &mdash; {active.author}{active.location ? `, ${active.location}` : ""}
              </p>
            </div>
            {testimonials.length > 1 && (
              <div className="mt-4 flex items-center justify-center gap-3">
                <button onClick={() => setIdx((i) => (i - 1 + testimonials.length) % testimonials.length)}
                  className="rounded-full border bg-card p-1.5 text-muted-foreground hover:text-foreground" aria-label="Previous testimonial" data-testid="testimonial-prev">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <div className="flex gap-1.5">
                  {testimonials.map((_, i) => (
                    <button key={i} onClick={() => setIdx(i)} aria-label={`Go to testimonial ${i + 1}`}
                      className={`h-2 w-2 rounded-full transition-colors ${i === idx ? "bg-primary" : "bg-muted-foreground/30"}`} />
                  ))}
                </div>
                <button onClick={() => setIdx((i) => (i + 1) % testimonials.length)}
                  className="rounded-full border bg-card p-1.5 text-muted-foreground hover:text-foreground" aria-label="Next testimonial" data-testid="testimonial-next">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </section>
  );
};
